import { useContext, useEffect, useMemo } from 'react';

import { AuthContext } from '@arrived/auth';

import { api, rewardsApi } from '../api';

const axiosInstances = [api, rewardsApi];

/**
 * When the AuthContext indicates that the user is authenticated, this hook will add an interceptor
 * that gets invoked on all API requests that appends an Authorization header with the user's
 * access token.
 */
export function useAuthenticationInterceptor() {
  const { isAuthenticated, getAccessToken } = useContext(AuthContext);

  /**
   * We choose to add the interceptor in a memo so that it gets added when the component calling
   * this hook gets rendered. Doing so in an effect means that the interceptor doesn't get added
   * until after the render lifecycle which may be too late for components down the application
   * which are trying to invoke API calls that need authentication.
   *
   * The resulting array of numbers represents a series of interceptor IDs that have been applied
   * to the axios instances. They typically start at 0, and increase by 1 each time a new
   * interceptor is created regardless of whether the previous one has been ejected from the API.
   */
  const authInterceptors = useMemo(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    return axiosInstances.map((instance) => {
      return instance.interceptors.request.use(async (config) => {
        if (!config.headers) {
          config.headers = {};
        }

        // Awaiting outside of the string template to avoid undefined issues on mobile
        const accessToken = await getAccessToken();

        // Note: Our BE Team says it's completely OK to always send JWT regardless of need
        if (accessToken) {
          config.headers['Authorization'] = `Bearer ${accessToken}`;
        }

        return config;
      });
    });
  }, [isAuthenticated]);

  /**
   * This effect is responsible for removing existing axios interceptors from all APIs either when
   * the component is unmounted, or the user's auth status changes (& thus changes the value of the
   * memoized interceptors value above).
   */
  useEffect(() => {
    return () => {
      axiosInstances.forEach((instance, idx) => {
        const axiosInterceptorId = authInterceptors?.length ? authInterceptors[idx] : undefined;
        // avoid mistreating 0 as falsy
        if (axiosInterceptorId !== undefined) {
          instance.interceptors.request.eject(axiosInterceptorId);
        }
      });
    };
  }, [authInterceptors]);
}
